import { QueryClient, type DefaultOptions } from '@tanstack/react-query';

/**
 * TanStack Query 기본 설정
 * 모든 설정을 명시적으로 정의하여 명확한 동작 보장
 */
export const defaultQueryConfig: DefaultOptions = {
  queries: {
    // 데이터가 stale 상태가 되기까지의 시간 (기본: 5분)
    staleTime: 1000 * 60 * 5,

    // 캐시 데이터가 메모리에 유지되는 시간 (기본: 10분)
    gcTime: 1000 * 60 * 10,

    // 실패 시 재시도 횟수 (기본: 3회)
    retry: 3,

    // 재시도 간격 계산 함수 (exponential backoff)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // 윈도우 포커스 시 자동 refetch (기본: true)
    refetchOnWindowFocus: true,

    // 네트워크 재연결 시 자동 refetch (기본: true)
    refetchOnReconnect: true,

    // 컴포넌트 마운트 시 자동 refetch (기본: true)
    refetchOnMount: true,

    // 에러 발생 시 자동으로 에러를 throw
    throwOnError: true,
  },
  mutations: {
    // Mutation 실패 시 재시도 횟수 (기본: 0)
    retry: 0,

    // 에러 발생 시 자동으로 에러를 throw하지 않음
    throwOnError: false,
  },
};

/**
 * QueryClient 인스턴스 생성 함수
 * 커스텀 설정을 받아서 기본 설정과 병합
 */
export const createQueryClient = (options?: DefaultOptions): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      ...defaultQueryConfig,
      ...options,
      queries: {
        ...defaultQueryConfig.queries,
        ...options?.queries,
      },
      mutations: {
        ...defaultQueryConfig.mutations,
        ...options?.mutations,
      },
    },
  });
};

/**
 * 싱글톤 QueryClient 인스턴스
 * 앱 전체에서 하나의 QueryClient를 사용하는 경우
 */
let queryClient: QueryClient | null = null;

export const getQueryClient = (): QueryClient => {
  if (!queryClient) {
    queryClient = createQueryClient();
  }
  return queryClient;
};

/**
 * QueryClient 재설정 (테스트나 특별한 경우에 사용)
 */
export const resetQueryClient = (): void => {
  if (queryClient) {
    queryClient.clear();
    queryClient = null;
  }
};
